/**
 * SunoWavJobsContext — фоновые задачи WAV-конвертации + localStorage-кэш.
 *
 * Три уровня устойчивости:
 *
 *  1) Polling живёт в App-level Provider — переживает закрытие любых
 *     шторок и навигацию внутри SPA.
 *
 *  2) Активные jobs сохраняются в localStorage как `{trackId: {requestId,
 *     startedAt, title}}`. При перезагрузке страницы Provider на mount
 *     восстанавливает их и продолжает polling.
 *
 *  3) Готовые wav_url тоже хранятся в localStorage (отдельным key'ом).
 *     UI (CardModal, /files, row) читает отсюда как fallback пока backend
 *     не отдаёт wav_url в API.
 *
 * Fallback-логика на таймауте: перед тем как отчаяться, делаем ОДИН запрос
 * в `/music/tracks?limit=100` — worker мог успеть сохранить wav_url пока
 * мы пропустили конкретную итерацию. Если там свежий URL для нашего трека —
 * считаем это успехом.
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { convertToWav, getUserTracks, type SunoTrack } from '../api/music'
import { waitForTask } from '../pages/generate/utils/waitForTask'
import { showToast } from '../components/Toast'
import { downloadAudioWithCover, safeFilename } from '../pages/bots/utils/audioWithCover'

const LS_PENDING_KEY = 'suno_wav_pending_v1'
const POLL_INTERVAL_MS = 10_000
// Сколько «старых» pending-jobs ресуме'ать после перезагрузки: если работа
// шла >30 мин — вероятнее всего убита, не ресуме'им.
const PENDING_MAX_AGE_MS = 30 * 60 * 1000

interface PendingJob {
	trackId: string
	requestId: string
	title: string | null
	/** Для embedding cover art в загруженный WAV после завершения job'а.
	 *  Опционально — если null, скачается без обложки. */
	imageUrl?: string | null
	tags?: string | null
	startedAt: number
}

interface SunoWavJobsValue {
	start: (track: SunoTrack) => void
	isRunning: (trackId: string) => boolean
	getCached: (trackId: string) => string | null
	/** Комбинированный resolver: backend-поле → localStorage cache. */
	resolveWav: (track: SunoTrack | null | undefined) => string | null
	/** Ручная проверка «зависшей» конвертации через /music/tracks. */
	recheck: (trackId: string) => Promise<void>
}

const SunoWavJobsContext = createContext<SunoWavJobsValue | null>(null)

// ─── localStorage helpers (ТОЛЬКО pending-jobs, не данные) ─────────
function loadPending(): Record<string, PendingJob> {
	try {
		const raw = localStorage.getItem(LS_PENDING_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return {}
		// Запуск с номером задачи живёт, пока сервер не ответил (план «Ждём ответа нейросети», Ф3). Срок — только у
		// записи восстановления без номера ('recovery'): спросить сервер о ней не о чем.
		const now = Date.now()
		const filtered: Record<string, PendingJob> = {}
		for (const [k, v] of Object.entries(parsed as Record<string, PendingJob>)) {
			if (v && typeof v === 'object' && v.requestId && v.startedAt) {
				if (v.requestId !== 'recovery' || now - v.startedAt < PENDING_MAX_AGE_MS) filtered[k] = v
			}
		}
		return filtered
	} catch {
		return {}
	}
}
function savePending(pending: Record<string, PendingJob>) {
	try {
		localStorage.setItem(LS_PENDING_KEY, JSON.stringify(pending))
	} catch {
		/* quota — запуск просто не восстановится после перезагрузки */
	}
}

// ─── utils ────────────────────────────────────────────────────────

/** Локальный wrapper над downloadAudioWithCover для WAV — делает вид
 *  подробных параметров не нужным в каждой call-site. */
async function downloadWavWithCover(
	wavUrl: string,
	title: string | null | undefined,
	imageUrl: string | null | undefined,
	genre: string | null | undefined,
): Promise<void> {
	await downloadAudioWithCover(wavUrl, imageUrl || null, safeFilename(title, 'wav'), {
		title,
		artist: 'Umnik AI',
		genre,
	})
}

export function SunoWavJobsProvider({ children }: { children: ReactNode }) {
	// In-memory session cache — источник истины для WAV это track.wav_url
	// из /tracks (backend). Локальный кэш нужен только чтобы если polling
	// только что закончился, сохранить URL до того как /tracks переотдаст
	// поле. После reload кэш теряется — это нормально, track.wav_url уже в
	// /tracks response.
	const [cache, setCache] = useState<Record<string, string>>({})
	const [runningIds, setRunningIds] = useState<Set<string>>(() => {
		// При старте Provider'а: если в pending есть jobs — они running.
		return new Set(Object.keys(loadPending()))
	})
	// Живой ref активных polling-циклов — защищает от двойного запуска.
	const activeRef = useRef<Set<string>>(new Set())
	// Ref на pending — чтобы эффекты не депендили на состоянии LS.
	const pendingRef = useRef<Record<string, PendingJob>>(loadPending())

	const writeCache = useCallback((trackId: string, wavUrl: string) => {
		setCache((prev) => {
			if (prev[trackId] === wavUrl) return prev
			return { ...prev, [trackId]: wavUrl }
		})
	}, [])

	const markRunning = useCallback((trackId: string, running: boolean) => {
		setRunningIds((prev) => {
			const next = new Set(prev)
			if (running) next.add(trackId)
			else next.delete(trackId)
			return next
		})
	}, [])

	const setPendingJob = useCallback((trackId: string, job: PendingJob | null) => {
		if (job) pendingRef.current[trackId] = job
		else delete pendingRef.current[trackId]
		savePending({ ...pendingRef.current })
	}, [])

	// ─── Recovery через /music/tracks ────────────────────────────
	// Используется когда Suno сказал «Wav record already exists» — это значит
	// что у Suno WAV есть, просто наш backend не сохранил его URL в suno_tracks.
	// Worker может досохранить wav_url при повторных поллингах, так что мы
	// периодически опрашиваем /tracks и ждём пока поле заполнится.
	const runTracksRecovery = useCallback(
		async (
			trackId: string,
			trackTitle: string | null,
			imageUrl?: string | null,
			tags?: string | null,
		) => {
			if (activeRef.current.has(trackId)) return
			activeRef.current.add(trackId)
			markRunning(trackId, true)
			setPendingJob(trackId, {
				trackId,
				requestId: 'recovery', // нет реального request_id
				title: trackTitle,
				imageUrl: imageUrl ?? null,
				tags: tags ?? null,
				startedAt: Date.now(),
			})

			const MAX_ATTEMPTS = 20 // ~3 мин при 10s интервале
			try {
				for (let i = 0; i < MAX_ATTEMPTS; i++) {
					await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
					try {
						const { tracks } = await getUserTracks({ limit: 100 })
						const found = tracks.find((t) => t.id === trackId)
						if (found?.wav_url) {
							writeCache(trackId, found.wav_url)
							showToast('WAV найден — скачиваем', 'success')
							await downloadWavWithCover(found.wav_url, trackTitle, found.image_url, found.tags)
							return
						}
					} catch {
						// ignore network hiccups
					}
				}
				// Номера задачи нет — спросить Suno не о чем, ждём ограниченно и говорим как есть: WAV у Suno есть,
				// ссылка ещё не дошла до библиотеки.
				showToast(
					'WAV у Suno уже есть, но ссылка ещё не дошла до библиотеки. Загляни чуть позже — если не появится, напиши в поддержку.',
					'info',
				)
			} finally {
				activeRef.current.delete(trackId)
				markRunning(trackId, false)
				setPendingJob(trackId, null)
			}
		},
		[markRunning, setPendingJob, writeCache],
	)

	// ─── Core polling loop ────────────────────────────────────────
	// Принимает либо новый job (если есть requestId), либо ресуме-job по
	// сохранённому requestId из pending.
	const runPolling = useCallback(
		async (
			trackId: string,
			requestId: string,
			trackTitle: string | null,
			imageUrl?: string | null,
			tags?: string | null,
		) => {
			if (activeRef.current.has(trackId)) return
			activeRef.current.add(trackId)
			markRunning(trackId, true)
			setPendingJob(trackId, {
				trackId,
				requestId,
				title: trackTitle,
				imageUrl: imageUrl ?? null,
				tags: tags ?? null,
				startedAt: pendingRef.current[trackId]?.startedAt ?? Date.now(),
			})

			let resolvedImageUrl: string | null = imageUrl ?? null
			let resolvedTags: string | null = tags ?? null
			try {
				// Ждём ответа сервера без потолка (waitForTask, план «Ждём ответа нейросети», Ф3).
				const s = await waitForTask(requestId, {
					minDelayMs: POLL_INTERVAL_MS,
					startedAt: pendingRef.current[trackId]?.startedAt,
				})
				if (!s) return
				if (s.status === 'failed') {
					showToast(s.error_message_ru || s.error_message || 'Конвертация не удалась', 'error')
					return
				}
				let wavUrl: string | null = s.result_urls?.[0] || s.result_url || null

				// Готово, а ссылки в ответе нет — worker мог сохранить wav_url прямо в suno_tracks; проверим
				// через /tracks. Заодно подтягиваем image_url/tags, если их не передали изначально (restore
				// после reload страницы).
				if (!wavUrl || !resolvedImageUrl) {
					try {
						const { tracks } = await getUserTracks({ limit: 100 })
						const found = tracks.find((t) => t.id === trackId)
						if (found) {
							if (!wavUrl && found.wav_url) wavUrl = found.wav_url
							if (!resolvedImageUrl) resolvedImageUrl = found.image_url
							if (!resolvedTags) resolvedTags = found.tags
						}
					} catch {
						// ignore
					}
				}

				if (!wavUrl) {
					showToast('Suno завершил конвертацию без ссылки на WAV — попробуй ещё раз.', 'error')
					return
				}

				writeCache(trackId, wavUrl)
				showToast('WAV готов — скачиваем...', 'success')
				await downloadWavWithCover(wavUrl, trackTitle, resolvedImageUrl, resolvedTags)
				// suno_tracks.wav_url теперь заполнен — уведомляем подписчиков
				// чтобы /tracks переотправил поле, бейдж WAV «закрепился».
				window.dispatchEvent(
					new CustomEvent('suno-wav-ready', { detail: { trackId } }),
				)
			} finally {
				activeRef.current.delete(trackId)
				markRunning(trackId, false)
				setPendingJob(trackId, null)
			}
		},
		[markRunning, setPendingJob, writeCache],
	)

	// ─── start(): внешний API ─────────────────────────────────────
	const start = useCallback(
		async (track: SunoTrack) => {
			const trackId = track.id

			// Уже есть готовый WAV — просто скачиваем, 0 токенов.
			const cached = track.wav_url || cache[trackId] || null
			if (cached) {
				downloadWavWithCover(cached, track.title, track.image_url, track.tags)
				return
			}

			if (activeRef.current.has(trackId)) {
				showToast('Конвертация уже идёт, жди...', 'info')
				return
			}

			showToast(
				`WAV конвертация для «${track.title?.trim() || 'трека'}» запущена`,
				'info',
			)

			const res = await convertToWav({ track_id: trackId })

			// Recovery-кейс от backend'а: он сам вытащил URL через record-info.
			// Сразу кэшируем + качаем, без polling.
			if (res.ok && res.wav_url) {
				writeCache(trackId, res.wav_url)
				showToast('WAV уже был готов — скачиваем', 'success')
				await downloadWavWithCover(res.wav_url, track.title, track.image_url, track.tags)
				return
			}

			// Frontend-recovery для «Wav record already exists»: это значит что
			// Suno уже сконвертировал WAV раньше, но у нас в БД `wav_url` пусто
			// (worker мог пропустить сохранение / backend recovery не задеплоен).
			// Стратегия: periodically опрашиваем /music/tracks — worker мог
			// продолжать досохранять даже после того как мы «забыли» про task.
			if (!res.ok && res.error && res.error.toLowerCase().includes('already exists')) {
				showToast('Suno уже конвертировал WAV — ищем ссылку в библиотеке...', 'info')
				runTracksRecovery(trackId, track.title, track.image_url, track.tags)
				return
			}

			if (!res.ok) {
				showToast(res.error || 'Ошибка запуска конвертации', 'error')
				return
			}

			if (!res.request_id) {
				showToast('Не удалось запустить конвертацию', 'error')
				return
			}

			// Сохраняем pending и запускаем стойкое polling.
			runPolling(trackId, res.request_id, track.title, track.image_url, track.tags)
		},
		[cache, runPolling, runTracksRecovery, writeCache],
	)

	// ─── recheck(): ручная проверка через /music/tracks ───────────
	const recheck = useCallback(
		async (trackId: string) => {
			try {
				const { tracks } = await getUserTracks({ limit: 100 })
				const found = tracks.find((t) => t.id === trackId)
				if (found?.wav_url) {
					writeCache(trackId, found.wav_url)
					setPendingJob(trackId, null)
					markRunning(trackId, false)
					activeRef.current.delete(trackId)
					showToast('WAV найден — скачиваем', 'success')
					await downloadWavWithCover(found.wav_url, found.title, found.image_url, found.tags)
				} else {
					showToast('WAV ещё не готов, попробуй позже', 'info')
				}
			} catch {
				showToast('Не удалось проверить статус', 'error')
			}
		},
		[writeCache, setPendingJob, markRunning],
	)

	// ─── Восстанавливаем pending-jobs при старте Provider'а ──────
	useEffect(() => {
		const pending = pendingRef.current
		const ids = Object.keys(pending)
		if (ids.length === 0) return

		// Даём небольшую задержку чтобы основной UI успел подняться.
		const t = setTimeout(() => {
			for (const trackId of ids) {
				const job = pending[trackId]
				if (!job) continue
				// 'recovery' = это был already-exists recovery через /tracks
				if (job.requestId === 'recovery') {
					runTracksRecovery(trackId, job.title, job.imageUrl, job.tags)
				} else {
					runPolling(trackId, job.requestId, job.title, job.imageUrl, job.tags)
				}
			}
		}, 500)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// ─── Value ────────────────────────────────────────────────────
	const isRunning = useCallback((trackId: string) => runningIds.has(trackId), [runningIds])

	const getCached = useCallback(
		(trackId: string) => cache[trackId] ?? null,
		[cache],
	)

	const resolveWav = useCallback(
		(track: SunoTrack | null | undefined) => {
			if (!track) return null
			return track.wav_url || cache[track.id] || null
		},
		[cache],
	)

	const value = useMemo<SunoWavJobsValue>(
		() => ({ start, isRunning, getCached, resolveWav, recheck }),
		[start, isRunning, getCached, resolveWav, recheck],
	)

	return (
		<SunoWavJobsContext.Provider value={value}>{children}</SunoWavJobsContext.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSunoWavJobs(): SunoWavJobsValue {
	const ctx = useContext(SunoWavJobsContext)
	if (!ctx) {
		return {
			start: () => {},
			isRunning: () => false,
			getCached: () => null,
			resolveWav: (t) => t?.wav_url ?? null,
			recheck: async () => {},
		}
	}
	return ctx
}
